import { ActionCreatorWithPayload, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { IDocDefinition } from '@/shared/models/doc-definition';

import { createAsyncActions } from './async-actions';
import {
  TSelectHolderId,
  TSelectUploadedDocs,
  TUploadFile,
  TGetSliceState,
} from './internal-types';
import { createSelectors } from './selectors';
import { IDocUploader, IDocUploaderForm } from './types';

const createUploadSlice = <TDocType extends string>(initialDocType: TDocType, name: string) =>
  createSlice({
    name: name,
    initialState: <IDocUploader<TDocType>>{
      docDefinitions: [],
      uploadingForm: {
        docType: initialDocType,
        expiresAt: undefined,
        file: undefined,
      },
      isPending: false,
    },
    reducers: {
      updateUploadFormValue: (
        state,
        action: PayloadAction<Partial<IDocUploaderForm<TDocType>>>,
      ) => {
        if (Object.keys(action.payload).length > 0) {
          Object.assign(state.uploadingForm, action.payload);
        }
      },
      setPending: (state, action: PayloadAction<boolean>) => {
        state.isPending = action.payload;
      },
      setDocDefinitions: (state, action: PayloadAction<IDocDefinition<TDocType>[]>) => {
        // Type 'TDocType' is not assignable to type 'Draft<TDocType>
        state.docDefinitions = action.payload as any;
      },
    },
  });

interface IParams<TDocType extends string> {
  namespace: string;
  eventTrackingType: string;
  actionSetBusy: ActionCreatorWithPayload<boolean>;
  selectHolderId: TSelectHolderId;
  selectUploadedDocs: TSelectUploadedDocs<TDocType>;
  uploadFile: TUploadFile<TDocType>;
  getSliceState: TGetSliceState<TDocType>;
}

const createUploadSliceUtils = <TDocType extends string>({
  namespace,
  eventTrackingType,
  actionSetBusy,
  selectHolderId,
  selectUploadedDocs,
  uploadFile,
  getSliceState,
}: IParams<TDocType>) => {
  const selectors = {
    ...createSelectors(getSliceState, selectUploadedDocs),
    getHolderId: selectHolderId,
  };
  const asyncActions = createAsyncActions({
    namespace,
    eventTrackingType,
    selectHolderId,
    selectIsBusy: selectors.isUploading,
    actionSetBusy,
    uploadFile,
  });

  return { selectors, asyncActions };
};

export { createUploadSlice, createUploadSliceUtils };
